<template>
    <div>
        <!-- Overlay -->
        <div v-if="open" class="bill-inbox-overlay" @click="close"></div>

        <!-- Panel latéral -->
        <div class="bill-inbox-sidebar" :class="{ open: open }">
            <div v-if="item" class="bill-inbox-sidebar-inner">

                <!-- ── Colonne gauche : formulaire + rapprochement ─────────── -->
                <div class="bill-inbox-col-left">

                    <!-- Header -->
                    <div class="bill-inbox-header">
                        <div class="d-flex align-items-center flex-wrap" style="gap: 6px;">
                            <span class="ix-state teal">{{ item.source }}</span>
                            <span class="ix-state" :class="documentTypeClass">{{ documentTypeLabel }}</span>
                            <span class="ix-state" :class="statusClass">{{ statusLabel }}</span>
                        </div>
                        <button class="ix-sidebar-btn-close" @click="close" type="button">
                            <i class="fal fa-times"></i>
                        </button>
                    </div>

                    <!-- Zone scrollable -->
                    <div class="bill-inbox-scroll">

                        <!-- ── Formulaire éditable (pending / needs_update) ── -->
                        <template v-if="isEditable">

                        <!-- Bandeau "ce qui a changé" en mode needs_update -->
                        <template v-if="item.sync_status === 'needs_update' && changes.length > 0">
                            <div class="bill-inbox-section-divider bill-inbox-changes-divider">
                                <i class="far fa-exclamation-circle mr-2"></i>
                                {{ changes.length }} {{ changes.length > 1 ? 'champs ont changé' : 'champ a changé' }} dans la source depuis le dernier import
                            </div>
                            <div class="bill-inbox-section bill-inbox-changes">
                                <div v-for="c in changes" :key="c.field" class="bill-inbox-change-row">
                                    <div class="ix-text-small ix-text-secondary mb-1">{{ c.label }}</div>
                                    <div class="d-flex align-items-center flex-wrap" style="gap: 6px;">
                                        <span class="bill-inbox-change-old">{{ c.old }}</span>
                                        <i class="far fa-arrow-right ix-text-secondary" style="font-size: 0.72em;"></i>
                                        <span class="bill-inbox-change-new">{{ c.new }}</span>
                                    </div>
                                </div>
                            </div>
                        </template>

                        <!-- Bandeau doublon potentiel (pending uniquement) -->
                        <template v-if="item.sync_status === 'pending' && duplicates.length > 0">
                            <div class="bill-inbox-section-divider bill-inbox-duplicate-divider">
                                <i class="far fa-copy mr-2"></i>
                                {{ duplicates.length > 1 ? duplicates.length + ' doublons potentiels détectés' : 'Doublon potentiel détecté' }}
                            </div>
                            <div class="bill-inbox-section bill-inbox-duplicates">
                                <div class="ix-text-secondary mb-2" style="font-size: 0.85em;">
                                    {{ isCreditNote ? 'Un avoir similaire existe déjà dans prod-up' : 'Une facture similaire existe déjà dans prod-up' }} (même fournisseur, même montant, même date).
                                </div>
                                <div v-for="dup in duplicates" :key="dup.number" class="bill-inbox-duplicate-row">
                                    <div class="d-flex align-items-center justify-content-between">
                                        <div>
                                            <span class="ix-text-bold">{{ dup.number }}</span>
                                            <span v-if="dup.state_label" class="ix-state ix-state-sm ml-2" :class="dup.state_color || ''">{{ dup.state_label }}</span>
                                        </div>
                                        <div class="ix-text-secondary ix-text-small">{{ dup.issued_at }}</div>
                                    </div>
                                    <div class="d-flex align-items-center justify-content-between mt-1">
                                        <span class="ix-text-bold" style="font-size: 0.9em;">{{ fmtAmt(dup.total_ttc) }} TTC</span>
                                        <a :href="dup.url" target="_blank" class="ix-link" style="font-size: 0.82em;">
                                            Voir <i class="far fa-external-link ml-1"></i>
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </template>

                        <!-- ── Étape fournisseur : tant qu'aucune société n'est rattachée,
                                c'est la seule décision à prendre. Le reste du formulaire
                                reste masqué pour ne pas disperser l'utilisateur. ── -->
                        <template v-if="supplierUnresolved">
                            <div class="bill-inbox-section-divider bill-inbox-supplier-divider">
                                <i class="far fa-building mr-2"></i>
                                Fournisseur à rattacher
                            </div>

                            <div v-if="!supplierContext" class="bill-inbox-section ix-text-secondary text-center py-3">
                                <i class="far fa-circle-notch fa-spin mr-1"></i> Recherche du fournisseur…
                            </div>

                            <div v-else class="bill-inbox-section bill-inbox-supplier">
                                <!-- Ce que la source connaît du fournisseur -->
                                <div class="bill-inbox-supplier-identity">
                                    <div class="ix-text-bold">
                                        <template v-if="supplierContext.identity">{{ supplierContext.identity.name }}</template>
                                        <template v-else>Aucun fournisseur dans {{ sourceLabel }}</template>
                                    </div>
                                    <div v-if="supplierContext.identity" class="ix-text-secondary" style="font-size: 0.85em;">
                                        <span>{{ sourceLabel }}</span>
                                        <span v-if="supplierContext.identity.siren"> · SIREN {{ supplierContext.identity.siren }}</span>
                                        <span v-if="supplierContext.identity.city"> · {{ supplierContext.identity.city }}</span>
                                    </div>
                                    <div class="ix-text-secondary mt-1" style="font-size: 0.82em;">
                                        {{ isCreditNote ? 'Avoir' : 'Facture' }}
                                        <template v-if="form.designation">« {{ truncate(form.designation, 34) }} »</template>
                                        <template v-if="form.date"> du {{ form.date }}</template>
                                        · {{ fmtAmt(item.data.amount) }}
                                    </div>
                                </div>

                                <div class="ix-text-secondary mt-2" style="font-size: 0.85em;">
                                    <template v-if="supplierContext.identity">
                                        Ce fournisseur n'a pas de fiche dans prod-up. Rattachez-le à une société
                                        existante, ou créez sa fiche.
                                        <template v-if="supplierContext.pending_count > 0">
                                            Votre choix s'appliquera aussi aux
                                            {{ supplierContext.pending_count }} autres
                                            {{ supplierContext.pending_count > 1 ? 'factures' : 'facture' }} en attente
                                            de ce fournisseur.
                                        </template>
                                    </template>
                                    <template v-else>
                                        Le document ne mentionne aucun fournisseur : il n'y a rien à rapprocher
                                        automatiquement. Choisissez la société concernée.
                                    </template>
                                </div>

                                <!-- Choix de la société -->
                                <template v-if="!linkCompany">
                                    <template v-if="supplierContext.candidates.length">
                                        <div class="ix-text-small ix-text-secondary mt-3 mb-1">
                                            Sociétés prod-up qui pourraient correspondre
                                        </div>
                                        <button v-for="c in supplierContext.candidates" :key="c.id"
                                                type="button" class="bill-inbox-candidate"
                                                :class="{ 'is-strong': c.strong }"
                                                @click="chooseCompany(c.id, c.name)">
                                            <div class="d-flex align-items-center justify-content-between">
                                                <span class="ix-text-bold">{{ truncate(c.name, 30) }}</span>
                                                <span class="ix-state" :class="c.strong ? 'green' : 'yellow'">
                                                    {{ c.strong ? 'Correspondance forte' : 'Possible' }}
                                                </span>
                                            </div>
                                            <div class="ix-text-secondary" style="font-size: 0.82em;">
                                                <span v-if="c.siren">SIREN {{ c.siren }}</span>
                                                <span v-if="c.siren && c.city"> · </span>
                                                <span v-if="c.city">{{ c.city }}</span>
                                                <span v-if="c.reasons.length"> · {{ c.reasons.join(', ') }}</span>
                                            </div>
                                        </button>
                                    </template>

                                    <!-- Recherche manuelle : le repli quand aucune suggestion ne convient -->
                                    <div class="form-group mt-3 mb-0">
                                        <label class="ix-text-small ix-text-secondary mb-1">
                                            {{ supplierContext.candidates.length ? 'Ou chercher une autre société' : 'Chercher la société dans prod-up' }}
                                        </label>
                                        <input ref="companyAutocomplete" type="text"
                                               class="form-control form-control-sm model-autocomplete input-company-billing"
                                               v-model="form.company_name"
                                               data-api-url="/api/companies/autocomplete"
                                               data-label="designation"
                                               data-value="name"
                                               data-force-value="true"
                                               data-target-input-id="inbox-company-id"
                                               placeholder="Nom de la société">
                                        <input id="inbox-company-id" type="hidden" v-model="selectedCompanyId">
                                    </div>

                                    <!-- Création en dernier recours : proposée après les pistes de
                                         rattachement, pour ne pas en faire le geste par défaut.
                                         Sans fournisseur dans la source, il n'y a rien à créer. -->
                                    <div v-if="supplierContext.identity" class="bill-inbox-supplier-create">
                                        <div class="ix-text-secondary mb-2" style="font-size: 0.85em;">
                                            Aucune de ces sociétés ne correspond ?
                                        </div>
                                        <button type="button" class="ix-btn ix-btn-small bordered blue"
                                                @click="createCompanyFromSource" :disabled="creatingCompany">
                                            <i :class="creatingCompany ? 'far fa-circle-notch fa-spin' : 'far fa-plus'"></i>
                                            Créer la fiche depuis {{ sourceLabel }}
                                        </button>

                                        <button type="button" class="ix-btn link ix-text-secondary mt-1"
                                                style="font-size: 0.82em; padding: 2px 0;"
                                                @click="showCreationPreview = !showCreationPreview">
                                            <i :class="showCreationPreview ? 'far fa-chevron-up' : 'far fa-chevron-down'" class="mr-1"></i>
                                            Voir ce qui sera créé
                                        </button>

                                        <table v-if="showCreationPreview" class="bill-inbox-field-table">
                                            <tr v-for="f in supplierContext.creation_fields" :key="f.label">
                                                <th>{{ f.label }}</th>
                                                <td>{{ f.value }}</td>
                                            </tr>
                                            <tr v-if="!supplierContext.identity.has_address">
                                                <th>Adresse</th>
                                                <td class="ix-text-secondary">Absente de la source, à ajouter ensuite</td>
                                            </tr>
                                        </table>
                                    </div>
                                </template>

                                <!-- Réconciliation avant rattachement -->
                                <template v-else>
                                    <div class="ix-text-small ix-text-secondary mt-3 mb-1">Société retenue</div>
                                    <div class="bill-inbox-supplier-identity d-flex align-items-center justify-content-between">
                                        <span class="ix-text-bold">{{ linkCompany.name }}</span>
                                        <button type="button" class="ix-btn link" style="font-size: 0.82em;"
                                                @click="cancelChoice">Changer</button>
                                    </div>

                                    <div v-if="reconcileLoading" class="ix-text-secondary text-center py-2" style="font-size: 0.85em;">
                                        <i class="far fa-circle-notch fa-spin mr-1"></i> Comparaison en cours…
                                    </div>

                                    <!-- Pour chaque champ divergent, une valeur l'emporte : deux
                                         cases à cocher autoriseraient « les deux » ou « aucune »,
                                         qui ne veulent rien dire. C'est donc un choix radio, et
                                         seule la reprise depuis la source s'accentue — conserver
                                         une valeur ne change rien et doit rester silencieux. -->
                                    <template v-else-if="reconcileFields.length">
                                        <table class="bill-inbox-reconcile">
                                            <thead>
                                                <tr>
                                                    <th></th>
                                                    <th>prod-up</th>
                                                    <th>{{ sourceLabel }}</th>
                                                </tr>
                                            </thead>
                                            <tr v-for="f in reconcileFields" :key="f.field"
                                                :class="{ 'is-updated': fieldChoice[f.field] === 'source' }">
                                                <th>{{ f.label }}</th>
                                                <td>
                                                    <label class="reconcile-cell"
                                                           :class="{ 'is-selected': fieldChoice[f.field] === 'current' }">
                                                        <input type="radio" :name="'reconcile-' + f.field"
                                                               value="current" v-model="fieldChoice[f.field]">
                                                        <span v-if="f.current">{{ f.current }}</span>
                                                        <span v-else class="is-empty">Vide</span>
                                                    </label>
                                                </td>
                                                <td>
                                                    <label class="reconcile-cell"
                                                           :class="{ 'is-selected': fieldChoice[f.field] === 'source' }">
                                                        <input type="radio" :name="'reconcile-' + f.field"
                                                               value="source" v-model="fieldChoice[f.field]">
                                                        <span>{{ f.incoming }}</span>
                                                    </label>
                                                </td>
                                            </tr>
                                        </table>
                                        <div class="bill-inbox-reconcile-summary">
                                            <template v-if="updatedFieldCount">
                                                <span class="ix-text-bold">{{ updatedFieldCount }}</span>
                                                {{ updatedFieldCount > 1 ? 'informations seront reprises' : 'information sera reprise' }}
                                                depuis {{ sourceLabel }}
                                            </template>
                                            <template v-else>Aucune information ne sera modifiée</template>
                                        </div>
                                    </template>

                                    <div v-else class="ix-text-secondary mt-2" style="font-size: 0.85em;">
                                        Rien à mettre à jour : la fiche prod-up est déjà à jour.
                                    </div>

                                    <button type="button" class="ix-btn ix-btn-small blue mt-3"
                                            @click="linkChosenCompany" :disabled="linking">
                                        <i :class="linking ? 'far fa-circle-notch fa-spin' : 'far fa-link'"></i>
                                        Rattacher ce fournisseur
                                    </button>
                                </template>

                                <div v-if="createCompanyError" class="mt-2" style="font-size: 0.82em; color: #e74c3c;">
                                    {{ createCompanyError }}
                                </div>
                            </div>
                        </template>

                        <template v-if="!supplierUnresolved">
                        <div v-if="supplierLinkMessage" class="bill-inbox-link-done">
                            <i class="far fa-check-circle mr-1"></i>{{ supplierLinkMessage }}
                        </div>
                        <div class="bill-inbox-section-divider">
                            {{ isCreditNote ? 'Avoir fournisseur' : 'Facture fournisseur' }}
                        </div>
                        <div class="bill-inbox-section">
                            <div class="form-group mb-2">
                                <label class="ix-text-small ix-text-secondary mb-1 required">Fournisseur</label>
                                <div class="input-group input-group-sm">
                                    <input ref="companyAutocomplete" type="text"
                                        class="form-control model-autocomplete input-company-billing"
                                        v-model="form.company_name"
                                        data-api-url="/api/companies/autocomplete"
                                        data-label="designation"
                                        data-value="name"
                                        data-force-value="true"
                                        data-target-input-id="inbox-company-id"
                                        placeholder="Nom du fournisseur">
                                    <div class="input-group-append">
                                        <button type="button" class="ix-btn ix-btn-icon blue"
                                                @click="openCompanyModal">
                                            <i class="far fa-plus"></i>
                                        </button>
                                    </div>
                                </div>
                                <input id="inbox-company-id" type="hidden" v-model="selectedCompanyId">
                            </div>

                            <div class="form-group mb-2">
                                <label class="ix-text-small ix-text-secondary mb-1 required">Adresse de facturation</label>
                                <div class="input-group input-group-sm">
                                    <input ref="addressAutocomplete" id="address_label" type="text"
                                           class="form-control model-autocomplete"
                                           v-model="form.address_label"
                                           :readonly="!selectedCompanyId"
                                           data-force-value="true"
                                           data-target-input-id="address_id"
                                           placeholder="Adresse de facturation">
                                    <div class="input-group-append">
                                        <button type="button" class="ix-btn ix-btn-icon blue"
                                                :disabled="!selectedCompanyId"
                                                @click="openAddressModal">
                                            <i class="far fa-plus"></i>
                                        </button>
                                    </div>
                                    <input id="address_id" type="hidden" v-model="selectedAddressId">
                                </div>
                                <div v-if="selectedCompanyId && !selectedAddressId" class="ix-text-secondary" style="font-size: 0.85em;">
                                    Sélectionnez ou créez une adresse de facturation.
                                </div>
                            </div>

                            <div class="row">
                                <div class="col-6">
                                    <div class="form-group mb-2">
                                        <label class="ix-text-small ix-text-secondary mb-1 required">Référence</label>
                                        <input type="text" class="form-control form-control-sm"
                                               v-model="form.reference"
                                               placeholder="N° facture source">
                                    </div>
                                </div>
                                <div class="col-6">
                                    <div class="form-group mb-2">
                                        <label class="ix-text-small ix-text-secondary mb-1 required">Désignation</label>
                                        <input type="text" class="form-control form-control-sm"
                                               v-model="form.designation"
                                               placeholder="Objet de la facture">
                                    </div>
                                </div>
                            </div>

                            <div class="row">
                                <div v-if="!isCreditNote" class="col-6">
                                    <div class="form-group mb-2">
                                        <label class="ix-text-small ix-text-secondary mb-1 required">Date d'émission</label>
                                        <input type="date" class="form-control form-control-sm"
                                               v-model="form.date"
                                               placeholder="JJ/MM/AAAA">
                                    </div>
                                </div>
                                <div :class="isCreditNote ? 'col-12' : 'col-6'">
                                    <div class="form-group mb-2">
                                        <label class="ix-text-small ix-text-secondary mb-1 required">Date d'échéance</label>
                                        <input type="date" class="form-control form-control-sm"
                                               v-model="form.deadline"
                                               placeholder="JJ/MM/AAAA">
                                    </div>
                                </div>
                            </div>

                            <!-- Bloc montants : HT / TVA / TTC -->
                            <div class="form-group mb-2" style="border: 1px solid #e8eaed; border-radius: 6px; padding: 10px 10px 8px;">
                                <label class="ix-text-small ix-text-secondary mb-2 d-block required">Montants</label>

                                <!-- Total HT -->
                                <label class="ix-text-small ix-text-secondary mb-1">Total HT</label>
                                <div class="input-group input-group-sm mb-2">
                                    <input type="number" step="0.01" class="form-control"
                                           v-model="form.amount_ht" :disabled="amountsLocked">
                                    <div class="input-group-append"><span class="input-group-text">€</span></div>
                                </div>
                                <div v-if="amountsLocked" class="ix-text-secondary mb-2" style="font-size: 0.8em;">
                                    <i class="far fa-lock mr-1"></i>Montants verrouillés (source Pennylane).
                                </div>

                                <!-- Séparateur TVA -->
                                <div class="ix-text-small ix-text-secondary mb-1" style="border-top: 1px solid #e8eaed; padding-top: 8px;">TVA</div>

                                <!-- Lignes TVA -->
                                <div v-for="(vat, index) in form.vats" :key="index"
                                     class="d-flex align-items-center mb-1" style="gap: 6px;">
                                    <div class="input-group input-group-sm" style="max-width: 115px;">
                                        <input type="number" step="0.1" min="0" class="form-control text-center"
                                               v-model="vat.rate" placeholder="Taux" :disabled="amountsLocked">
                                        <div class="input-group-append"><span class="input-group-text">%</span></div>
                                    </div>
                                    <div class="input-group input-group-sm flex-grow-1">
                                        <input type="number" step="0.01" class="form-control"
                                               v-model="vat.amount" placeholder="Montant" :disabled="amountsLocked">
                                        <div class="input-group-append">
                                            <span class="input-group-text">€</span>
                                            <button v-if="form.vats.length > 1" type="button"
                                                    class="ix-btn ix-btn-sm icon"
                                                    :disabled="amountsLocked"
                                                    @click="removeVatRow(index)">
                                                <i class="far fa-trash"></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <!-- Ajouter + warning -->
                                <div class="d-flex align-items-center justify-content-between mt-1 mb-2">
                                    <button v-if="!amountsLocked" type="button" class="ix-btn link"
                                            style="font-size: 0.82em; padding: 2px 0;"
                                            @click="addVatRow">
                                        <i class="far fa-plus"></i> Ajouter une TVA
                                    </button>
                                    <span v-if="vatWarning" style="font-size: 0.78em; color: #e67e22;">
                                        <i class="far fa-exclamation-triangle mr-1"></i>{{ vatWarning }}
                                    </span>
                                </div>

                                <!-- Total TVA -->
                                <label class="ix-text-small ix-text-secondary mb-1">Total TVA</label>
                                <div class="input-group input-group-sm mb-2">
                                    <input type="number" step="0.01" class="form-control" disabled
                                           :value="computedTotalVat">
                                    <div class="input-group-append"><span class="input-group-text">€</span></div>
                                </div>

                                <!-- Total TTC -->
                                <div style="border-top: 1px solid #e8eaed; padding-top: 8px;">
                                    <label class="ix-text-small ix-text-secondary mb-1">Total TTC</label>
                                    <div class="input-group input-group-sm">
                                        <input type="number" step="0.01" class="form-control" readonly
                                               :value="computedTotalTtc">
                                        <div class="input-group-append"><span class="input-group-text">€</span></div>
                                    </div>
                                </div>
                            </div>

                            <div class="ix-text-secondary" style="font-size: 0.82em;">
                                * Champs obligatoires pour l'import dans prod-up : fournisseur, adresse de facturation, référence, désignation, {{ isCreditNote ? 'date d\'échéance' : 'date d\'émission, date d\'échéance' }}, total HT, TVA, total TTC.
                            </div>
                        </div>
                        </template>
                        </template>

                        <!-- Rapprochement en lecture seule (needs_update : factures et avoirs) -->
                        <template v-if="item.sync_status === 'needs_update' && !supplierUnresolved">
                            <div class="bill-inbox-section-divider">
                                <i class="far fa-link mr-2"></i>
                                Rapprochement actuel (lecture seule)
                            </div>
                            <div class="bill-inbox-section bill-inbox-match-summary">

                                <!-- Facture fournisseur -->
                                <template v-if="!isCreditNote">
                                    <template v-if="currentMatch && currentMatch.bill">
                                        <div class="ix-text-secondary mb-2" style="font-size: 0.9em;">
                                            <i class="far fa-paperclip mr-1"></i>
                                            Cette facture est importée dans prod-up sous
                                            <a :href="currentMatch.bill.url" target="_blank" class="ix-link ml-1">
                                                {{ currentMatch.bill.number }}
                                            </a>
                                        </div>

                                        <div v-if="currentMatch.expenses && currentMatch.expenses.length > 0" class="ix-text-secondary mb-2" style="font-size: 0.9em;">
                                            <span class="ix-text-bold">Rapprochement commande fournisseur :</span>
                                            <span v-for="(expense, index) in currentMatch.expenses" :key="expense.number" class="ml-1">
                                                <template v-if="index > 0">, </template>
                                                <a :href="expense.url" target="_blank" class="ix-link">{{ expense.number }}</a>
                                            </span>
                                        </div>
                                        <div v-else class="ix-text-secondary mb-2" style="font-size: 0.9em;">
                                            <i class="far fa-info-circle mr-1"></i>
                                            Cette facture est importée sans bon de commande.
                                        </div>

                                        <a :href="currentMatch.bill.url" target="_blank" class="ix-link" style="font-size: 0.9em;">
                                            <i class="far fa-arrow-right mr-1"></i>Modifier le rapprochement sur la fiche facture
                                        </a>
                                    </template>
                                    <div v-else class="ix-text-secondary" style="font-size: 0.9em;">
                                        <i class="far fa-info-circle mr-1"></i>
                                        Rapprochement actuel indisponible.
                                    </div>
                                </template>

                                <!-- Avoir fournisseur -->
                                <template v-else>
                                    <template v-if="currentMatch && currentMatch.credit_supplier">
                                        <div class="ix-text-secondary mb-2" style="font-size: 0.9em;">
                                            <i class="far fa-paperclip mr-1"></i>
                                            Cet avoir est importé dans prod-up sous
                                            <a :href="currentMatch.credit_supplier.url" target="_blank" class="ix-link ml-1">
                                                {{ currentMatch.credit_supplier.number }}
                                            </a>
                                        </div>

                                        <div v-if="currentMatch.linked_bill" class="ix-text-secondary mb-2" style="font-size: 0.9em;">
                                            <span class="ix-text-bold">Facture liée :</span>
                                            <a :href="currentMatch.linked_bill.url" target="_blank" class="ix-link ml-1">
                                                {{ currentMatch.linked_bill.number }}
                                            </a>
                                        </div>
                                        <div v-else class="ix-text-secondary mb-2" style="font-size: 0.9em;">
                                            <i class="far fa-info-circle mr-1"></i>
                                            Cet avoir n'est pas lié à une facture.
                                        </div>

                                        <a :href="currentMatch.credit_supplier.url" target="_blank" class="ix-link" style="font-size: 0.9em;">
                                            <i class="far fa-arrow-right mr-1"></i>Modifier le rapprochement sur la fiche avoir fournisseur
                                        </a>
                                    </template>
                                    <div v-else class="ix-text-secondary" style="font-size: 0.9em;">
                                        <i class="far fa-info-circle mr-1"></i>
                                        Rapprochement actuel indisponible.
                                    </div>
                                </template>

                            </div>
                        </template>

                        <!-- ── Séparateur Import / Rapprochement ── -->
                        <template v-if="isEditable && item.sync_status === 'pending' && !supplierUnresolved">
                            <div class="bill-inbox-section-divider">
                                <i class="far fa-link mr-2"></i>
                                {{ isCreditNote ? 'Lier à une facture fournisseur' : 'Rapprochement commande fournisseur' }}
                            </div>
                        </template>

                        <!-- ── Section rapprochement (pending uniquement) ── -->
                        <template v-if="isEditable && item.sync_status === 'pending' && !supplierUnresolved">

                            <!-- Facture fournisseur : suggestions commande fournisseur -->
                            <div v-if="!isCreditNote" class="bill-inbox-section" style="border-top: none;">


                                <div v-if="suggestionsLoading" class="text-center py-3">
                                    <i class="far fa-circle-notch fa-spin ix-text-secondary"></i>
                                </div>

                                <template v-else>
                                    <!-- Choix 1 : rapprocher avec des commandes existantes -->
                                    <div class="d-flex align-items-center mb-2" style="gap: 8px;">
                                        <input type="radio" id="match-expenses-bc" value="expenses"
                                               v-model="selectedMatchMode" class="flex-shrink-0">
                                        <label for="match-expenses-bc" class="mb-0" style="cursor: pointer;">
                                            <i class="far fa-link mr-1"></i>
                                            Rapprocher avec une ou plusieurs commandes existantes
                                        </label>
                                    </div>

                                    <!-- Replié en v-show : la sélection et les montants saisis sont
                                         conservés si l'utilisateur bascule sur un autre mode, et
                                         l'autocomplete jQuery reste attaché au champ. -->
                                    <div v-show="computedMatchMode === 'expenses'" class="bill-inbox-match-panel mb-3">

                                    <!-- Étape 1 : choisir les commandes fournisseurs -->
                                    <div v-if="suggestions.length > 0" class="mb-3">
                                        <div class="ix-text-small ix-text-secondary mb-2">
                                            {{ suggestions.length }} commande{{ suggestions.length > 1 ? 's' : '' }} fournisseur
                                            suggérée{{ suggestions.length > 1 ? 's' : '' }} - cochez celles à rapprocher
                                        </div>

                                        <div v-for="s in suggestions" :key="s.id"
                                             class="bill-inbox-suggestion"
                                             :class="{ active: isExpenseSelected(s.id) }">
                                            <div class="d-flex align-items-start" style="gap: 8px;">
                                                <input type="checkbox" :id="'match-sug-bc-' + s.id"
                                                       :checked="isExpenseSelected(s.id)"
                                                       @change="toggleExpense(s)"
                                                       class="bill-inbox-suggestion-check flex-shrink-0">
                                                <div class="flex-grow-1" style="min-width: 0;">
                                                    <div class="d-flex align-items-center justify-content-between flex-wrap"
                                                         style="gap: 4px; cursor: pointer;"
                                                         @click="toggleSuggestionDetail(s.id)">
                                                        <div style="min-width: 0;">
                                                            <span class="ix-text-bold">{{ s.number }}</span>
                                                            <span class="ix-text-secondary ml-2" style="font-size: 0.9em;">{{ truncate(s.designation, 28) }}</span>
                                                        </div>
                                                        <div class="d-flex align-items-center flex-shrink-0" style="gap: 6px;">
                                                            <span class="ix-text-bold ix-nowrap">{{ fmtAmt(s.total_remaining) }}</span>
                                                            <span class="ix-state ix-state-sm" :class="confidenceClass(s.confidence)">
                                                                {{ confidenceLabel(s.confidence) }}
                                                            </span>
                                                            <i class="far" :class="expandedSuggestionId === s.id ? 'fa-chevron-up' : 'fa-chevron-down'"
                                                               style="font-size: 0.8em;"></i>
                                                        </div>
                                                    </div>

                                                    <!-- Mini-show détails BC -->
                                                    <div v-if="expandedSuggestionId === s.id" class="bill-inbox-suggestion-detail">
                                                        <div class="row" style="font-size: 0.85em;">
                                                            <div class="col-6 mb-1">
                                                                <div class="ix-text-secondary">Fournisseur</div>
                                                                <div>{{ s.company || '-' }}</div>
                                                            </div>
                                                            <div class="col-6 mb-1">
                                                                <div class="ix-text-secondary">Date validé</div>
                                                                <div>{{ s.validated_at || '-' }}</div>
                                                            </div>
                                                            <div class="col-6 mb-1">
                                                                <div class="ix-text-secondary">Total HT</div>
                                                                <div>{{ fmtAmt(s.total_ht) }}</div>
                                                            </div>
                                                            <div class="col-6 mb-1">
                                                                <div class="ix-text-secondary">Total TTC</div>
                                                                <div>{{ fmtAmt(s.total_ttc) }}</div>
                                                            </div>
                                                            <div class="col-6 mb-1">
                                                                <div class="ix-text-secondary">Déjà facturé</div>
                                                                <div>{{ fmtAmt(s.total_invoiced) }}</div>
                                                            </div>
                                                            <div class="col-6 mb-1">
                                                                <div class="ix-text-secondary">Restant</div>
                                                                <div class="ix-text-bold">{{ fmtAmt(s.total_remaining) }}</div>
                                                            </div>
                                                        </div>
                                                        <div class="progress mb-1" style="height: 5px;">
                                                            <div class="progress-bar" role="progressbar"
                                                                 :style="{ width: Math.min(s.percent_invoiced, 100) + '%' }"></div>
                                                        </div>
                                                        <div class="ix-text-secondary mb-2" style="font-size: 0.8em;">
                                                            {{ s.percent_invoiced }}% facturé
                                                        </div>
                                                        <a :href="s.url" target="_blank" class="ix-link" style="font-size: 0.85em;">
                                                            Voir le bon de commande <i class="far fa-arrow-right"></i>
                                                        </a>
                                                        <div v-if="s.reason" class="ix-text-secondary mt-1" style="font-size: 0.8em;">
                                                            <i class="far fa-info-circle mr-1"></i>{{ s.reason }}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div v-else class="alert alert-warning py-2 mb-3" style="font-size: 0.9em;">
                                        <i class="far fa-exclamation-triangle mr-1"></i>
                                        Aucune commande fournisseur correspondante trouvée.
                                    </div>

                                    <!-- Autocomplete commande fournisseur : ajoute une ligne au panier -->
                                    <div class="mb-3">
                                        <input ref="matchAutocomplete" type="text"
                                               class="form-control form-control-sm model-autocomplete"
                                               v-model="autocompleteQuery"
                                               :data-api-url="matchAutocompleteUrl"
                                               data-label="full_designation"
                                               data-value="full_designation"
                                               data-force-value="true"
                                               data-target-input-id="inbox-match-id"
                                               placeholder="Ajouter une autre commande : n°, désignation, fournisseur...">
                                        <input id="inbox-match-id" type="hidden" v-model="selectedMatchId">
                                        <div v-if="autocompleteLoading" class="ix-text-secondary ix-text-small py-1">
                                            <i class="far fa-circle-notch fa-spin mr-1"></i> Recherche...
                                        </div>
                                    </div>

                                    <!-- Étape 2 : ventiler le montant de la facture sur les commandes retenues -->
                                    <div v-if="selectedExpenses.length > 0" class="bill-inbox-affectation mb-2">
                                        <div class="bill-inbox-affectation-title">
                                            Montant de la facture à imputer sur chaque commande
                                        </div>

                                        <div v-for="(e, index) in selectedExpenses" :key="e.id"
                                             class="bill-inbox-affectation-row">
                                            <div class="d-flex align-items-baseline justify-content-between" style="gap: 6px;">
                                                <span class="ix-text-bold" style="min-width: 0;">{{ e.number }}</span>
                                                <span class="ix-text-secondary ix-nowrap" style="font-size: 0.8em;">
                                                    restant facturable {{ fmtAmt(e.total_remaining) }}
                                                </span>
                                            </div>
                                            <div v-if="e.designation" class="ix-text-secondary" style="font-size: 0.85em;">
                                                {{ truncate(e.designation, 40) }}
                                            </div>
                                            <div class="d-flex align-items-center mt-1" style="gap: 6px;">
                                                <label :for="'affect-amount-' + e.id"
                                                       class="mb-0 ix-text-secondary ix-nowrap" style="font-size: 0.8em;">
                                                    Imputé
                                                </label>
                                                <div class="input-group input-group-sm flex-grow-1">
                                                    <input :id="'affect-amount-' + e.id" type="number" step="0.01"
                                                           class="form-control form-control-sm text-right"
                                                           v-model="e.amount">
                                                    <div class="input-group-append">
                                                        <span class="input-group-text">€ TTC</span>
                                                    </div>
                                                </div>
                                                <button type="button" class="ix-btn ix-btn-sm icon flex-shrink-0"
                                                        title="Retirer cette commande"
                                                        @click="removeExpense(index)">
                                                    <i class="far fa-trash"></i>
                                                </button>
                                            </div>
                                        </div>

                                        <div class="bill-inbox-affectation-summary">
                                            <span>Imputé <span class="ix-text-bold">{{ fmtAmt(totalAffected) }}</span></span>
                                            <span class="ix-text-secondary">TTC facture {{ fmtAmt(computedTotalTtc) }}</span>
                                            <span :class="Math.abs(remainingToAffect) >= 0.01 ? 'ix-text-orange ix-text-bold' : 'ix-text-secondary'">
                                                reste {{ fmtAmt(remainingToAffect) }}
                                            </span>
                                        </div>
                                    </div>

                                    <div v-if="affectationWarning" class="alert alert-warning py-2 mb-0" style="font-size: 0.85em;">
                                        <i class="far fa-exclamation-triangle mr-1"></i>{{ affectationWarning }}
                                    </div>

                                    </div><!-- /bill-inbox-match-panel -->

                                    <!-- Choix 2 : générer une commande fournisseur à ventiler -->
                                    <div class="d-flex align-items-center mb-2" style="gap: 8px;">
                                        <input type="radio" id="match-generate-bc" value="generate"
                                               v-model="selectedMatchMode" class="flex-shrink-0">
                                        <label for="match-generate-bc" class="mb-0" style="cursor: pointer;">
                                            <i class="far fa-magic mr-1"></i>
                                            Générer une commande fournisseur
                                            <span class="ix-text-secondary">(ligne générique, à ventiler)</span>
                                        </label>
                                    </div>

                                    <!-- Choix 3 : sans commande fournisseur -->
                                    <div class="d-flex align-items-center mb-2" style="gap: 8px;">
                                        <input type="radio" id="match-no-bc" value="none"
                                               v-model="selectedMatchMode" class="flex-shrink-0">
                                        <label for="match-no-bc" class="mb-0 ix-text-secondary" style="cursor: pointer;">
                                            Importer la facture sans commande fournisseur
                                        </label>
                                    </div>
                                </template>
                            </div>

                            <!-- Avoir fournisseur : lien à une facture -->
                            <div v-else class="bill-inbox-section" style="border-top: none;">

                                <div v-if="suggestionsLoading" class="text-center py-3">
                                    <i class="far fa-circle-notch fa-spin ix-text-secondary"></i>
                                </div>

                                <template v-else>
                                    <div v-if="suggestions.length > 0" class="mb-3">
                                        <div class="ix-text-small ix-text-secondary mb-2">
                                            {{ suggestions.length }} facture{{ suggestions.length > 1 ? 's' : '' }} trouvée{{ suggestions.length > 1 ? 's' : '' }}
                                        </div>

                                        <div v-for="s in suggestions" :key="s.id"
                                             class="bill-inbox-suggestion"
                                             :class="{ active: computedMatchMode === 'suggestion' && computedSelectedMatchId === s.id }"
                                             @click="selectedMatchMode = 'suggestion:' + s.id"
                                             style="cursor: pointer;">
                                            <div class="d-flex align-items-start" style="gap: 8px;">
                                                <input type="radio" :id="'match-sug-bill-' + s.id" :value="'suggestion:' + s.id"
                                                       v-model="selectedMatchMode"
                                                       class="mt-1 flex-shrink-0"
                                                       @click.stop>
                                                <div class="flex-grow-1">
                                                    <div>
                                                        <span class="ix-text-bold">{{ s.number }}</span>
                                                        <span v-if="s.state_label" class="ix-state ix-state-sm ml-2"
                                                              :class="s.state_color || ''">{{ s.state_label }}</span>
                                                    </div>
                                                    <div v-if="s.designation" class="ix-text-secondary" style="font-size: 0.9em;">
                                                        {{ truncate(s.designation, 40) }}
                                                    </div>
                                                    <div class="ix-text-small ix-text-secondary">
                                                        {{ s.company || '' }}
                                                        <span v-if="s.issued_at"> · {{ s.issued_at }}</span>
                                                        · <span class="ix-text-bold">{{ fmtAmt(s.total_ttc) }}</span> TTC
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div v-else class="alert alert-warning py-2 mb-3" style="font-size: 0.9em;">
                                        <i class="far fa-exclamation-triangle mr-1"></i>
                                        Aucune facture correspondante trouvée.
                                    </div>

                                    <!-- Autocomplete factures -->
                                    <div class="mb-2">
                                        <div class="d-flex align-items-center mb-2" style="gap: 8px;">
                                            <input type="radio" id="match-autocomplete-bill" value="autocomplete"
                                                   v-model="selectedMatchMode" class="flex-shrink-0">
                                            <label for="match-autocomplete-bill" class="mb-0" style="cursor: pointer;">
                                                <i class="far fa-search mr-1"></i> Rechercher une facture fournisseur
                                            </label>
                                        </div>
                                        <template v-if="computedMatchMode === 'autocomplete'">
                                              <input ref="matchAutocomplete" type="text" class="form-control form-control-sm mb-1 model-autocomplete"
                                                   v-model="autocompleteQuery"
                                                  :data-api-url="matchAutocompleteUrl"
                                                  data-label="full_designation"
                                                  data-value="full_designation"
                                                  data-force-value="true"
                                                  data-target-input-id="inbox-match-id"
                                                   placeholder="N° facture, désignation...">
                                              <input id="inbox-match-id" type="hidden" v-model="selectedMatchId">
                                            <div v-if="autocompleteLoading" class="ix-text-secondary ix-text-small py-1">
                                                <i class="far fa-circle-notch fa-spin mr-1"></i> Recherche...
                                            </div>
                                            <div v-for="r in autocompleteResults" :key="r.id"
                                                 class="bill-inbox-autocomplete-item"
                                                 :class="{ active: computedSelectedMatchId === r.id && computedMatchMode === 'autocomplete' }"
                                                 @click="selectedMatchMode = 'autocomplete:' + r.id; autocompleteQuery = r.full_designation || r.number || r.label || ''">
                                                <span class="ix-text-bold">{{ r.number || r.label }}</span>
                                                <span v-if="r.designation" class="ix-text-secondary ml-1" style="font-size: 0.9em;">
                                                    {{ truncate(r.designation, 30) }}
                                                </span>
                                            </div>
                                        </template>
                                    </div>

                                    <!-- Option : sans lien -->
                                    <div class="d-flex align-items-center mb-2" style="gap: 8px;">
                                        <input type="radio" id="match-bill-none" value="none"
                                               v-model="selectedMatchMode" class="flex-shrink-0">
                                        <label for="match-bill-none" class="mb-0 ix-text-secondary" style="cursor: pointer;">
                                            Créer l'avoir sans lien avec une facture
                                        </label>
                                    </div>
                                </template>
                            </div>
                        </template>

                        <!-- ── Vue up_to_date / on_hold ──────────────────── -->
                        <bill-inbox-read-only
                            v-else-if="item.sync_status === 'up_to_date' || item.sync_status === 'on_hold'"
                            :item="item"
                            :inbox-log="item.sync_status === 'up_to_date' ? inboxLog : null"
                            :log-loading="item.sync_status === 'up_to_date' ? logLoading : false"
                            :is-credit-note="isCreditNote"
                            :source-supplier-name="sourceSupplierName"
                        ></bill-inbox-read-only>

                        <!-- Feedback erreur -->
                        <div v-if="syncError" class="alert alert-danger py-2 mx-0 mb-3" style="font-size: 0.9em;">
                            <i class="far fa-exclamation-triangle mr-1"></i>
                            {{ syncError }}
                        </div>


                    </div><!-- /scroll -->

                    <!-- Message de validation si suggestions existent mais aucune sélectionnée -->
                    <div v-if="matchValidationError && isEditable" class="alert alert-warning py-2 mx-0 mb-2" style="font-size: 0.85em;">
                        <i class="far fa-info-circle mr-1"></i>
                        {{ matchValidationError }}
                    </div>

                    <!-- Footer avec bouton principal -->
                    <div v-if="isEditable && !canSync" class="bill-inbox-footer">
                        <div class="text-center ix-text-secondary py-2" style="font-size: 0.85em;">
                            <i class="far fa-lock mr-1"></i> Vous n'avez pas la permission d'importer des factures.
                        </div>
                    </div>
                    <div v-else-if="isEditable" class="bill-inbox-footer">
                        <!-- Tant que le fournisseur n'est pas rattaché, l'action sur la
                             facture n'est pas seulement impossible : elle n'a pas encore
                             lieu d'être. On l'annonce plutôt que d'afficher un bouton mort. -->
                        <div v-if="supplierUnresolved" class="text-center ix-text-secondary py-2" style="font-size: 0.85em;">
                            <i class="far fa-arrow-up mr-1"></i>
                            Rattachez le fournisseur pour poursuivre
                        </div>
                        <button v-else type="button" class="ix-btn blue" style="width: 100%;"
                                @click="confirmSync" :disabled="!canSave || syncing"
                                :title="!canSave ? 'Remplissez les champs obligatoires et sélectionnez une option de rapprochement' : ''">
                            <i :class="syncing ? 'far fa-circle-notch fa-spin' : 'far fa-check'"></i>
                            <template v-if="item.sync_status === 'needs_update'">Mettre à jour</template>
                            <template v-else-if="isCreditNote">Importer l'avoir</template>
                            <template v-else>Importer et rapprocher</template>
                        </button>
                        <div class="mt-2 text-center">
                            <button type="button" class="ix-btn link ix-text-secondary" style="font-size: 0.85em;"
                                    @click="confirmHold" :disabled="holding">
                                <i :class="holding ? 'far fa-circle-notch fa-spin' : 'far fa-pause-circle'"></i>
                                Mettre en attente
                            </button>
                        </div>
                        <div v-if="holdError" class="ix-text-secondary mt-1 text-center" style="font-size: 0.82em; color: #e74c3c;">
                            {{ holdError }}
                        </div>
                    </div>

                    <!-- Footer on_hold -->
                    <div v-else-if="isOnHold" class="bill-inbox-footer text-center">
                        <div class="ix-text-secondary mb-2" style="font-size: 0.85em;">
                            <i class="far fa-pause-circle mr-1"></i> Cet élément est en attente.
                        </div>
                        <button type="button" class="ix-btn" @click="confirmUnhold" :disabled="holding">
                            <i :class="holding ? 'far fa-circle-notch fa-spin' : 'far fa-play-circle'"></i>
                            Remettre à traiter
                        </button>
                        <div v-if="holdError" class="ix-text-secondary mt-1" style="font-size: 0.82em; color: #e74c3c;">
                            {{ holdError }}
                        </div>
                    </div>
                </div>

                <!-- ── Colonne droite : PDF viewer ─────────────────────────── -->
                <bill-inbox-pdf-viewer :pdf-url="pdfUrl"></bill-inbox-pdf-viewer>

            </div>
        </div>
    </div>
</template>

<script>
import { fmtAmt, truncate, normalizeDateForInput, confidenceLabel, confidenceClass } from './billInboxUtils.js';
import BillInboxPdfViewer from './BillInboxPdfViewer.vue';
import BillInboxReadOnly  from './BillInboxReadOnly.vue';

export default {
    name: 'BillInboxSidebar',

    components: { BillInboxPdfViewer, BillInboxReadOnly },

    props: {
        syncUrl:                { type: String, required: true },
        inboxBaseUrl:           { type: String, required: true },
        suggestMatchBaseUrl:    { type: String, required: true },
        suggestBillBaseUrl:     { type: String, required: true },
        pdfProxyBaseUrl:        { type: String, required: true },
        expenseAutocompleteUrl: { type: String, required: true },
        billsAutocompleteUrl:   { type: String, required: true },
        csrfToken:              { type: String, required: true },
        canSync:                { type: Boolean, default: false },
    },

    data() {
        return {
            open:                 false,
            item:                 null,
            form:                 {},
            selectedCompanyId:    null,
            selectedAddressId:    null,
            suggestions:          [],
            suggestionsLoading:   false,
            selectedMatchId:      null,
            matchMode:            'suggestion',
            selectedMatchMode:    null,
            // Facture fournisseur : panier de BC affectés - [{ id, number, designation,
            // total_ht, total_ttc, total_remaining, amount }]. Les avoirs restent en mono-lien.
            selectedExpenses:     [],
            autocompleteQuery:    '',
            autocompleteResults:  [],
            autocompleteLoading:  false,
            autocompleteTimer:    null,
            expandedSuggestionId: null,
            // Étape fournisseur : la source ne correspond à aucune société prod-up
            supplierContext:       null,
            supplierResolvedByUser: false,
            showCreationPreview:   false,
            linkCompany:           null,
            reconcileFields:       [],
            // { nomDuChamp: 'current' | 'source' } — quelle valeur l'emporte
            fieldChoice:           {},
            reconcileLoading:      false,
            linking:               false,
            creatingCompany:       false,
            createCompanyError:    null,
            supplierLinkMessage:   null,
            inboxLog:             null,
            logLoading:           false,
            vatLinesLoading:      false,
            changes:              [],
            currentMatch:         null,
            duplicates:           [],
            pdfUrl:               null,
            syncing:              false,
            syncError:            null,
            holding:              false,
            holdError:            null,
            duplicatesTimer:      null,
        };
    },

    computed: {
        isCreditNote() {
            return this.item && this.item.data && this.item.data.document_type === 'credit_note';
        },

        isOnHold() {
            return this.item && this.item.sync_status === 'on_hold';
        },

        // Sources dont les montants sont garantis fiables (e-facturation) : on bloque l'édition.
        amountsLocked() {
            const lockedSources = ['pennylane'];
            return this.item && lockedSources.includes(this.item.source);
        },

        isEditable() {
            return this.item && (
                this.item.sync_status === 'pending' ||
                this.item.sync_status === 'needs_update'
            );
        },

        documentTypeLabel() {
            if (!this.item) return '';
            return this.isCreditNote ? 'Avoir' : 'Facture';
        },

        documentTypeClass() {
            return this.isCreditNote ? 'purple' : '';
        },

        statusLabel() {
            if (!this.item) return '';
            const labels = {
                pending:      'À traiter',
                needs_update: 'Mise à jour disponible',
                up_to_date:   'À jour',
                on_hold:      'En attente',
            };
            return labels[this.item.sync_status] || this.item.sync_status;
        },

        statusClass() {
            if (!this.item) return '';
            const classes = {
                pending:      'orange',
                needs_update: 'blue',
                up_to_date:   'green',
                on_hold:      'grey',
            };
            return classes[this.item.sync_status] || '';
        },

        matchAutocompleteUrl() {
            if (!this.item) return '';
            return this.isCreditNote
                ? this.billsAutocompleteUrl
                : this.expenseAutocompleteUrl;
        },

        sourceSupplierName() {
            if (!this.item || !this.item.data) return '';
            return this.item.data.company_name || this.item.data.supplier_name || '';
        },

        computedMatchMode() {
            if (!this.selectedMatchMode) return null;
            // Factures : 'expenses' (panier multi-BC), 'generate' ou 'none'
            if (this.selectedMatchMode === 'expenses')    return 'expenses';
            if (this.selectedMatchMode.startsWith('suggestion:'))  return 'suggestion';
            if (this.selectedMatchMode.startsWith('autocomplete:')) return 'autocomplete';
            if (this.selectedMatchMode === 'generate')    return 'generate';
            if (this.selectedMatchMode === 'none')        return 'none';
            if (this.selectedMatchMode === 'autocomplete') return 'autocomplete';
            return null;
        },

        computedSelectedMatchId() {
            if (!this.selectedMatchMode) return null;
            if (this.selectedMatchMode.startsWith('suggestion:') || this.selectedMatchMode.startsWith('autocomplete:')) {
                return parseInt(this.selectedMatchMode.split(':')[1], 10);
            }
            return null;
        },

        matchValidationError() {
            if (this.isEditable && this.item.sync_status === 'pending' && !this.selectedMatchMode) {
                return 'Veuillez choisir la méthode de rapprochement.';
            }
            return null;
        },

        totalAffected() {
            return Math.round(
                this.selectedExpenses.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0) * 100
            ) / 100;
        },

        remainingToAffect() {
            return Math.round((this.computedTotalTtc - this.totalAffected) * 100) / 100;
        },

        // Avertissement non bloquant : l'utilisateur peut vouloir n'affecter qu'une partie.
        affectationWarning() {
            if (this.selectedExpenses.length === 0) return null;
            if (this.selectedExpenses.some(e => (parseFloat(e.amount) || 0) <= 0)) {
                return 'Chaque commande fournisseur doit avoir un montant affecté supérieur à 0.';
            }
            if (Math.abs(this.remainingToAffect) >= 0.01) {
                return 'La somme des montants affectés ne correspond pas au total TTC de la facture.';
            }
            return null;
        },

        computedTotalVat() {
            if (!this.form.vats) return 0;
            return Math.round(
                this.form.vats.reduce((sum, v) => sum + (parseFloat(v.amount) || 0), 0) * 100
            ) / 100;
        },

        computedTotalTtc() {
            const ht  = parseFloat(this.form.amount_ht) || 0;
            return Math.round((ht + this.computedTotalVat) * 100) / 100;
        },

        vatWarning() {
            if (!this.form.vats) return null;
            const standard = [0, 2.1, 5.5, 8.5, 10, 20];
            const unusual = this.form.vats.filter(v => {
                const rate   = parseFloat(v.rate);
                const amount = parseFloat(v.amount);
                return !isNaN(rate) && amount !== 0 && !standard.includes(rate);
            });
            if (unusual.length === 0) return null;
            return 'Taux inhabituel : ' + unusual.map(v => v.rate + ' %').join(', ');
        },

        sourceLabel() {
            return (this.item && (this.item.data.source_label || this.item.source)) || 'la source';
        },

        // Champs dont la valeur de la source l'emporte : c'est ce qui sera écrit.
        selectedFields() {
            return Object.keys(this.fieldChoice).filter(f => this.fieldChoice[f] === 'source');
        },

        updatedFieldCount() {
            return this.selectedFields.length;
        },

        // Le fournisseur de la source n'a pas de fiche prod-up : l'utilisateur doit
        // le rattacher avant de voir le reste du formulaire. On se base sur l'état à
        // l'ouverture, et non sur selectedCompanyId, pour que le panneau reste affiché
        // le temps de la réconciliation.
        supplierUnresolved() {
            return this.isEditable && this.item && !this.item.company_id && !this.supplierResolvedByUser;
        },

        canSave() {
            const requiredDate = this.isCreditNote ? this.form.deadline : this.form.date;
            if (!requiredDate || !this.computedTotalTtc) {
                return false;
            }
            if (!this.selectedCompanyId) {
                return false;
            }
            if (this.item && this.item.sync_status === 'pending') {
                if (!this.selectedAddressId) return false;
                if (!this.selectedMatchMode) return false;
                if (this.computedMatchMode === 'expenses') {
                    if (this.selectedExpenses.length === 0) return false;
                    if (this.selectedExpenses.some(e => (parseFloat(e.amount) || 0) <= 0)) return false;
                }
            }
            return true;
        },
    },

    mounted() {
        window.addEventListener('open-inbox-sidebar', (e) => {
            this.openItem(e.detail);
        });

        if (window.$) {
            window.$('#addAddressModal').on('hidden.bs.modal.billInboxSidebar', () => {
                if (this.open && this.selectedCompanyId) {
                    this.loadCompanyDefaults(this.selectedCompanyId);
                }
            });
        }
    },

    beforeDestroy() {
        if (window.$) {
            window.$('#addAddressModal').off('hidden.bs.modal.billInboxSidebar');
            window.$('#button-submit-company').off('click.billInbox');
            window.$('#modal_is_enterprise').off('change.billInbox');
        }
    },

    watch: {
        computedMatchMode(newValue) {
            if (newValue === 'autocomplete') {
                this.$nextTick(() => {
                    this.initMatchAutocomplete();

                    if (this.$refs.matchAutocomplete && window.$) {
                        window.$(this.$refs.matchAutocomplete).autocomplete('search', '');
                    }
                });
            }
        },

        // Facture : le champ de recherche BC est toujours affiché une fois les
        // suggestions chargées - on branche l'autocomplete dès que le DOM existe.
        suggestionsLoading(newValue) {
            if (!newValue && !this.isCreditNote) {
                this.$nextTick(() => { this.initMatchAutocomplete(); });
            }
        },

        // Le champ de recherche société n'est rendu qu'une fois le contexte
        // fournisseur chargé, donc après le branchement fait à l'ouverture.
        supplierContext(newValue) {
            if (newValue) {
                this.$nextTick(() => { this.initCompanyAutocomplete(); });
            }
        },

        // Retour à la liste des suggestions : le champ est recréé.
        linkCompany(newValue) {
            if (!newValue) {
                this.$nextTick(() => { this.initCompanyAutocomplete(); });
            }
        },

        selectedCompanyId(newValue, oldValue) {
            if (!newValue || newValue === oldValue) {
                return;
            }

            // Société choisie à l'autocomplétion pendant l'étape fournisseur : elle
            // passe par la réconciliation, comme une suggestion cliquée.
            if (this.supplierUnresolved && !this.linkCompany) {
                this.chooseCompany(newValue, this.form.company_name);
                return;
            }

            this.loadCompanyDefaults(newValue);
            if (this.item && this.item.sync_status === 'pending') {
                this.loadDuplicates();
            }
        },

        computedTotalTtc(newValue, oldValue) {
            if (this.item && this.item.sync_status === 'pending' && newValue !== oldValue) {
                this.debouncedLoadDuplicates();
            }
        },

        'form.date'(newValue, oldValue) {
            if (this.item && this.item.sync_status === 'pending' && newValue !== oldValue) {
                this.debouncedLoadDuplicates();
            }
        },
    },

    methods: {

        // ── Utilitaires importés ────────────────────────────────────────────
        fmtAmt,
        truncate,
        normalizeDateForInput,
        confidenceLabel,
        confidenceClass,

        // ─── Ouverture / fermeture ──────────────────────────────────────────

        openItem(item) {
            this.item                 = item;
            this.form                 = this.buildForm(item);
            this.selectedCompanyId    = item.company_id || null;
            this.selectedAddressId    = null;
            this.suggestions          = [];
            this.selectedMatchId      = null;
            this.matchMode            = 'suggestion';
            this.selectedMatchMode    = null;
            this.selectedExpenses     = [];
            this.autocompleteQuery    = '';
            this.autocompleteResults  = [];
            this.expandedSuggestionId = null;
            this.inboxLog             = null;
            this.changes              = [];
            this.currentMatch         = null;
            this.duplicates           = [];
            this.syncError            = null;
            this.holdError            = null;
            this.supplierContext        = null;
            this.supplierResolvedByUser = false;
            this.showCreationPreview    = false;
            this.linkCompany            = null;
            this.reconcileFields        = [];
            this.fieldChoice            = {};
            this.createCompanyError     = null;
            this.supplierLinkMessage    = null;

            // Sans société rattachée, le nom porté par la source ne désigne aucune
            // fiche prod-up : le laisser dans le champ ferait croire à une sélection.
            if (!item.company_id) {
                this.form.company_name = '';
            }

            this.open = true;
            document.body.classList.add('ix-sidebar-open');

            this.$nextTick(() => {
                this.initAutocompleteInputs();
            });

            // Le rapprochement est fait côté serveur : sans société retenue, c'est
            // qu'aucune correspondance n'est certaine et que l'utilisateur doit trancher.
            if (!this.selectedCompanyId && this.isEditable) {
                this.loadSupplierContext();
            }

            if (item.sync_status === 'pending') {
                this.loadSuggestions();
                this.loadVatLines();
                this.loadDuplicates();
            } else if (item.sync_status === 'needs_update') {
                this.loadVatLines();
                this.loadChanges();
            } else if (item.sync_status === 'up_to_date') {
                this.loadLog();
            }
            // on_hold : aucun chargement asynchrone nécessaire

            const hasFile = item.data && item.data.public_file_url;
            this.pdfUrl = hasFile ? this.pdfProxyBaseUrl + '/' + item.id + '/pdf' : null;
        },

        close() {
            this.open = false;
            document.body.classList.remove('ix-sidebar-open');
        },

        // ─── Formulaire ─────────────────────────────────────────────────────

        buildForm(item) {
            const d   = item.data || {};
            // Avoirs Pennylane : montants négatifs → on travaille toujours en valeur absolue
            const ht  = Math.abs(parseFloat(d.currency_amount_before_tax) || 0);
            const vat = Math.abs(parseFloat(d.currency_tax) || 0);
            const ttc = Math.abs(parseFloat(d.amount) || 0);
            const rate = (ht !== 0)
                ? Math.round((vat / ht) * 100 * 10) / 10
                : 20;
            const vats = vat !== 0
                ? [{ rate, amount: vat }]
                : [{ rate: 20, amount: 0 }];

            return {
                company_id:    item.company_id || null,
                company_name:  d.company_name || d.supplier_name || '',
                address_id:    null,
                address_label: '',
                reference:     d.invoice_number || '',
                designation:   d.label || '',
                date:          this.normalizeDateForInput(d.date),
                deadline:      this.normalizeDateForInput(d.deadline),
                amount_ht:     ht || '',
                amount_ttc:    ttc || '',
                vats,
            };
        },

        // ─── Fournisseur non retrouvé ────────────────────────────────────────

        loadSupplierContext() {
            this.supplierContext = null;

            // Le contexte est toujours renseigné, même sans fournisseur dans la source :
            // le laisser nul figerait le panneau sur son indicateur de chargement.
            const fallback = { identity: null, candidates: [], creation_fields: [], pending_count: 0 };

            fetch(this.inboxBaseUrl + '/' + this.item.id + '/supplier-context', {
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
            })
                .then(r => r.json())
                .then(data => { this.supplierContext = Object.assign(fallback, data || {}); })
                .catch(() => { this.supplierContext = fallback; });
        },

        // Une société retenue n'est pas encore rattachée : on présente d'abord ce que
        // la source apporterait à la fiche, l'utilisateur arbitre puis valide.
        chooseCompany(id, name) {
            this.linkCompany      = { id: id, name: name };
            this.reconcileFields  = [];
            this.fieldChoice      = {};
            this.reconcileLoading = true;

            fetch(this.inboxBaseUrl + '/' + this.item.id + '/reconcile-fields?company_id=' + id, {
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
            })
                .then(r => r.json())
                .then(data => {
                    this.reconcileFields = data.fields || [];

                    // Objet reconstruit d'un bloc : Vue 2 ne suit pas les clés
                    // ajoutées après coup sur un objet existant.
                    const choice = {};
                    this.reconcileFields.forEach(f => {
                        choice[f.field] = f.preselected ? 'source' : 'current';
                    });
                    this.fieldChoice = choice;

                    if (data.company) this.linkCompany.name = data.company.name;
                })
                .catch(() => { this.reconcileFields = []; })
                .finally(() => { this.reconcileLoading = false; });
        },

        cancelChoice() {
            this.linkCompany       = null;
            this.reconcileFields   = [];
            this.fieldChoice       = {};
            this.selectedCompanyId = null;
            this.form.company_name = '';
            if (this.$refs.companyAutocomplete) {
                window.$(this.$refs.companyAutocomplete).val('');
            }
        },

        linkChosenCompany() {
            this.linking            = true;
            this.createCompanyError = null;

            this.postCompanyAction('/link-company', {
                company_id: this.linkCompany.id,
                fields:     this.selectedFields,
            }).finally(() => { this.linking = false; });
        },

        createCompanyFromSource() {
            this.creatingCompany    = true;
            this.createCompanyError = null;

            this.postCompanyAction('/create-company', {})
                .finally(() => { this.creatingCompany = false; });
        },

        /**
         * Récapitule ce que le rattachement a réellement produit : l'utilisateur vient
         * d'agir sur une fiche CRM et, potentiellement, sur d'autres factures que celle
         * qu'il a sous les yeux.
         */
        buildLinkMessage(body) {
            const name  = body.company.name;
            const parts = [body.created
                ? 'Fiche « ' + name + ' » créée depuis ' + this.sourceLabel + '.'
                : 'Fournisseur rattaché à « ' + name + ' ».'];

            if (body.updated) {
                parts.push(body.updated > 1
                    ? body.updated + ' informations reprises depuis ' + this.sourceLabel + '.'
                    : '1 information reprise depuis ' + this.sourceLabel + '.');
            }

            if (body.propagated) {
                parts.push(body.propagated > 1
                    ? body.propagated + ' autres factures en attente ont été mises à jour.'
                    : '1 autre facture en attente a été mise à jour.');
            }

            return parts.join(' ');
        },

        /**
         * Rattachement et création partagent la même suite : la société devient celle
         * de la facture, l'étape fournisseur se referme et le formulaire apparaît.
         */
        postCompanyAction(path, payload) {
            return fetch(this.inboxBaseUrl + '/' + this.item.id + path, {
                method: 'POST',
                headers: {
                    'X-CSRF-TOKEN':     this.csrfToken,
                    'X-Requested-With': 'XMLHttpRequest',
                    'Accept':           'application/json',
                    'Content-Type':     'application/json',
                },
                body: JSON.stringify(payload),
            })
                .then(r => r.json().then(body => ({ ok: r.ok, body })))
                .then(({ ok, body }) => {
                    if (!ok || body.status === 'error') {
                        this.createCompanyError = body.message || "Le rattachement du fournisseur a échoué.";
                        return;
                    }

                    this.selectedCompanyId = body.company.id;
                    this.form.company_name = body.company.name;

                    if (body.company.address_id) {
                        this.selectedAddressId  = body.company.address_id;
                        this.form.address_label = body.company.address_label;
                    }

                    this.supplierLinkMessage    = this.buildLinkMessage(body);
                    this.supplierResolvedByUser = true;
                    this.supplierContext        = null;
                    this.linkCompany            = null;

                    // La ligne du tableau porte encore l'ancien état : sans ça, rouvrir
                    // la sidebar redemanderait un rattachement déjà fait.
                    this.item.company_id = body.company.id;
                    window.dispatchEvent(new CustomEvent('inbox-item-status-changed', {
                        detail: { id: this.item.id, company_id: body.company.id },
                    }));

                    // Les champs du formulaire viennent d'apparaître : leurs
                    // autocomplétions doivent être rebranchées sur les nouveaux inputs.
                    this.$nextTick(() => {
                        this.initAutocompleteInputs();
                        if (this.$refs.companyAutocomplete) {
                            window.$(this.$refs.companyAutocomplete).val(this.form.company_name);
                        }
                        if (this.$refs.addressAutocomplete && this.form.address_label) {
                            window.$(this.$refs.addressAutocomplete).val(this.form.address_label);
                        }
                    });

                    this.loadDuplicates();
                })
                .catch(() => {
                    this.createCompanyError = "Le rattachement du fournisseur a échoué.";
                });
        },

        // ─── Suggestions ────────────────────────────────────────────────────

        loadSuggestions() {
            this.suggestionsLoading = true;
            this.suggestions        = [];

            const url = this.isCreditNote
                ? this.suggestBillBaseUrl  + '/' + this.item.id + '/suggest-bill'
                : this.suggestMatchBaseUrl + '/' + this.item.id + '/suggest-match';

            fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
                .then(r => r.json())
                .then(data => {
                    this.suggestions = this.isCreditNote
                        ? (data.bills || [])
                        : (data.suggestions || []);
                    // Facture : le rapprochement BC est le cas dominant, on pré-sélectionne
                    // le mode pour ne pas imposer un clic supplémentaire. Aucun risque
                    // d'import involontaire : `canSave` exige au moins un BC sélectionné.
                    if (!this.isCreditNote && !this.selectedMatchMode) {
                        this.selectedMatchMode = 'expenses';
                    }
                })
                .catch(() => {})
                .finally(() => { this.suggestionsLoading = false; });
        },

        toggleSuggestionDetail(id) {
            this.expandedSuggestionId = this.expandedSuggestionId === id ? null : id;
        },

        // ─── Panier de commandes fournisseurs affectées ─────────────────────

        isExpenseSelected(id) {
            return this.selectedExpenses.some(e => e.id === id);
        },

        toggleExpense(expense) {
            const index = this.selectedExpenses.findIndex(e => e.id === expense.id);
            if (index === -1) {
                this.addExpense(expense);
            } else {
                this.removeExpense(index);
            }
        },

        /**
         * Ajoute un BC au panier. Le montant affecté par défaut couvre le reste à
         * ventiler sur la facture, dans la limite du restant facturable du BC.
         */
        addExpense(expense) {
            if (this.isExpenseSelected(expense.id)) return;

            const remainingOnExpense = parseFloat(expense.total_remaining);
            const hasRemaining       = !isNaN(remainingOnExpense) && remainingOnExpense > 0;

            // Reste à ventiler sur la facture, plafonné au restant facturable du BC.
            // Si la facture est déjà entièrement ventilée, on propose le restant du BC :
            // à l'utilisateur d'arbitrer, l'avertissement d'écart le signalera.
            let amount = this.remainingToAffect > 0
                ? (hasRemaining ? Math.min(this.remainingToAffect, remainingOnExpense) : this.remainingToAffect)
                : (hasRemaining ? remainingOnExpense : 0);

            amount = Math.max(0, Math.round(amount * 100) / 100);

            this.selectedExpenses.push({
                id:              expense.id,
                number:          expense.number || expense.full_designation || expense.label || '',
                designation:     expense.designation || '',
                total_ht:        expense.total_ht,
                total_ttc:       expense.total_ttc,
                total_remaining: expense.total_remaining,
                amount,
            });

            this.selectedMatchMode = 'expenses';
        },

        // Le mode reste 'expenses' même panier vide : c'est un choix explicite de
        // l'utilisateur via le radio, `canSave` bloque tant que rien n'est sélectionné.
        removeExpense(index) {
            this.selectedExpenses.splice(index, 1);
        },

        // ─── Détection de doublons ───────────────────────────────────────────

        debouncedLoadDuplicates() {
            clearTimeout(this.duplicatesTimer);
            this.duplicatesTimer = setTimeout(() => this.loadDuplicates(), 600);
        },

        loadDuplicates() {
            this.duplicates = [];
            const params = new URLSearchParams();
            if (this.selectedCompanyId) params.set('company_id', this.selectedCompanyId);
            if (this.computedTotalTtc)  params.set('amount', this.computedTotalTtc);
            if (this.form.date)         params.set('date', this.form.date);

            fetch(this.suggestMatchBaseUrl + '/' + this.item.id + '/check-duplicate?' + params.toString(), {
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
            })
                .then(r => r.json())
                .then(data => { this.duplicates = data.duplicates || []; })
                .catch(() => {});
        },

        // ─── Changements détectés depuis le dernier import ──────────────────

        loadChanges() {
            const url = this.suggestMatchBaseUrl + '/' + this.item.id + '/changes';

            fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
                .then(r => r.json())
                .then(data => {
                    this.changes = data.changes || [];
                    this.currentMatch = data.match || null;
                })
                .catch(() => {
                    this.changes = [];
                    this.currentMatch = null;
                });
        },

        // ─── Lignes TVA depuis la source ─────────────────────────────────────

        loadVatLines() {
            this.vatLinesLoading = true;
            const url = this.suggestMatchBaseUrl + '/' + this.item.id + '/vat-lines';

            fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
                .then(r => r.json())
                .then(data => {
                    const vats = data.vats || [];
                    if (vats.length > 0) {
                        // Travailler toujours en valeur absolue (avoirs : montants négatifs côté source)
                        this.form.vats = vats.map(v => ({
                            rate:   parseFloat(v.rate)   || 0,
                            amount: Math.abs(parseFloat(v.amount) || 0),
                        }));
                    }
                })
                .catch(() => {})
                .finally(() => { this.vatLinesLoading = false; });
        },

        // ─── Log (up_to_date) ────────────────────────────────────────────────

        loadLog() {
            this.logLoading = true;
            this.inboxLog   = null;

            fetch(this.suggestMatchBaseUrl + '/' + this.item.id + '/log', {
                headers: { 'X-Requested-With': 'XMLHttpRequest' }
            })
                .then(r => r.json())
                .then(data => { this.inboxLog = data; })
                .catch(() => {})
                .finally(() => { this.logLoading = false; });
        },

        // ─── Autocomplete ────────────────────────────────────────────────────

        initAutocompleteInputs() {
            this.initCompanyAutocomplete();
            this.initMatchAutocomplete();
            this.initAddressAutocomplete();
        },

        initCompanyAutocomplete() {
            if (!this.$refs.companyAutocomplete || !window.$) return;

            const $input = window.$(this.$refs.companyAutocomplete);
            if ($input.data('ui-autocomplete')) $input.autocomplete('destroy');

            $input.autocomplete({
                minLength: 0,
                source: (request, response) => {
                    window.$.ajax({
                        url: $input.data('api-url'),
                        type: 'get',
                        dataType: 'json',
                        headers: this.autocompleteHeaders(),
                        data: { q: request.term },
                        success: (data) => {
                            response(window.$.map(data, (item) => Object.assign(item, {
                                label: item.designation || item.name || '',
                                value: item.name || item.designation || '',
                            })));
                        },
                    });
                },
                change: (event, item) => {
                    if (!item.item) {
                        if ($input.data('force-value') == true) {
                            window.$(event.target).val('');
                            this.form.company_name = '';
                        }
                        this.selectedCompanyId = null;
                        this.selectedAddressId = null;
                        this.form.address_label = '';
                        const hidden = window.$('#' + $input.data('target-input-id'));
                        if (hidden.length) hidden.val('').change();
                    }
                },
                focus: () => false,
                select: (event, item) => {
                    this.selectedCompanyId = item.item.id;
                    this.form.company_name = item.item.value;
                    window.$(event.target).val(item.item.value).trigger('input');
                    setTimeout(() => { window.$(event.target).blur(); });
                },
            }).focus(function () {
                $input.autocomplete('search', '');
            });

            $input.autocomplete('instance')._renderItem = this.renderCompanyAutocomplete;
        },

        initAddressAutocomplete() {
            if (!this.$refs.addressAutocomplete || !window.$ || !this.selectedCompanyId) return;

            const $input = window.$(this.$refs.addressAutocomplete);
            if ($input.data('ui-autocomplete')) $input.autocomplete('destroy');

            $input.autocomplete({
                minLength: 0,
                source: (request, response) => {
                    window.$.ajax({
                        url: '/api/addresses/autocomplete',
                        type: 'get',
                        dataType: 'json',
                        headers: this.autocompleteHeaders(),
                        data: { q: request.term, company_id: this.selectedCompanyId },
                        success: (data) => {
                            response(window.$.map(data, (item) => Object.assign(item, {
                                label: item.designation || item.line_formatted || '',
                                value: item.line_formatted || item.designation || '',
                            })));
                        },
                    });
                },
                change: (event, item) => {
                    if (!item.item) {
                        if ($input.data('force-value') == true) {
                            window.$(event.target).val('');
                            this.form.address_label = '';
                        }
                        this.selectedAddressId = null;
                        const hidden = window.$('#' + $input.data('target-input-id'));
                        if (hidden.length) hidden.val('').change();
                    }
                },
                focus: () => false,
                select: (event, item) => {
                    this.selectedAddressId = item.item.id;
                    this.form.address_label = item.item.value;
                    const hidden = window.$('#' + $input.data('target-input-id'));
                    if (hidden.length) hidden.val(item.item.id).change();
                    window.$(event.target).val(item.item.value).trigger('input');
                    setTimeout(() => { window.$(event.target).blur(); });
                },
            }).focus(function () {
                $input.autocomplete('search', '');
            });
        },

        loadCompanyDefaults(companyId) {
            if (!companyId || !window.$) return;

            fetch('/api/companies/' + companyId + '/defaults', {
                headers: this.autocompleteHeaders(),
                method: 'GET',
            })
                .then((response) => response.ok ? response.json() : null)
                .then((data) => {
                    const billingAddress = data && data.data ? data.data.billing_address : null;
                    if (billingAddress) {
                        this.selectedAddressId = billingAddress.id;
                        this.form.address_label = billingAddress.line_formatted || '';
                    } else {
                        this.selectedAddressId = null;
                        this.form.address_label = '';
                    }
                    this.$nextTick(() => { this.initAddressAutocomplete(); });
                })
                .catch(() => {
                    this.selectedAddressId = null;
                    this.form.address_label = '';
                });
        },

        openCompanyModal() {
            if (!window.$) return;

            // Réinitialiser le formulaire
            window.$('#autocomplete_company_name').val(this.form.company_name || '');
            window.$('#modal_phone').val('');
            window.$('#modal_email').val('');
            window.$('#modal_firstname').val('');
            window.$('#modal_is_enterprise').val(1);
            window.$('#modal_contact_section').hide();

            // Toggle particulier/entreprise
            window.$('#modal_is_enterprise').off('change.billInbox').on('change.billInbox', function () {
                window.$('#modal_contact_section').toggle(window.$(this).val() == 0);
            });

            // Submit : on gère nous-mêmes pour récupérer la société dans Vue
            window.$('#button-submit-company').off('click.billInbox').on('click.billInbox', () => {
                const name = window.$('#autocomplete_company_name').val().trim();
                if (!name) return;

                const submitUrl = window.$('#button-submit-company').data('submit-url');
                fetch(submitUrl, {
                    method:  'POST',
                    headers: this.autocompleteHeaders(),
                    body:    JSON.stringify({
                        name,
                        is_enterprise:         window.$('#modal_is_enterprise').val(),
                        firstname:             window.$('#modal_firstname').val() || null,
                        email:                 window.$('#modal_email').val() || null,
                        phone:                 window.$('#modal_phone').val() || null,
                        warranty_cap_inactive: true,
                        is_provider:           true,
                        state:                 2,
                        gender:                1,
                    }),
                })
                    .then(r => r.json())
                    .then(data => {
                        if (!data.data) return;
                        this.selectedCompanyId = data.data.id;
                        this.form.company_name = data.data.name;
                        if (this.$refs.companyAutocomplete) {
                            window.$(this.$refs.companyAutocomplete).val(data.data.name);
                        }
                        window.$('#addCompanyModal').modal('hide');
                    })
                    .catch(() => {});
            });

            window.$('#addCompanyModal').modal('show');
        },

        openAddressModal() {
            if (!this.selectedCompanyId || !window.$) return;

            window.$('#modal_address_type').val('billing');
            window.$('#modal_address_company_name').val(this.form.company_name || '');
            window.$('#modal_address_company_id').val(this.selectedCompanyId);
            window.$('#modal_address_is_default_true').prop('checked', true);
            window.$('#addAddressModal').modal('show');
        },

        initMatchAutocomplete() {
            if (!this.$refs.matchAutocomplete || !window.$ || !this.matchAutocompleteUrl) return;

            const vm = this;
            const $input = window.$(this.$refs.matchAutocomplete);
            if ($input.data('ui-autocomplete')) $input.autocomplete('destroy');

            $input.autocomplete({
                minLength: 0,
                source: (request, response) => {
                    const params = { q: request.term };

                    // Facture : recherche sur l'ensemble des BC (le moteur de suggestions
                    // couvre déjà le fournisseur retenu, ce champ sert justement à aller
                    // chercher ailleurs). On masque seulement ceux déjà dans le panier.
                    if (!this.isCreditNote && this.selectedExpenses.length > 0) {
                        params['!id'] = this.selectedExpenses.map(e => e.id).join(';');
                    }

                    window.$.ajax({
                        url: this.matchAutocompleteUrl,
                        type: 'get',
                        dataType: 'json',
                        headers: this.autocompleteHeaders(),
                        data: params,
                        success: (data) => {
                            const records = Array.isArray(data) ? data : (data.data || []);
                            response(window.$.map(records, (item) => Object.assign(item, {
                                label: item.full_designation || item.number || item.designation || '',
                                value: item.full_designation || item.number || item.designation || '',
                            })));
                        },
                    });
                },
                change: (event, item) => {
                    if (!item.item) {
                        if ($input.data('force-value') == true) {
                            window.$(event.target).val('');
                            this.autocompleteQuery = '';
                        }
                        this.selectedMatchId = null;
                        const hidden = window.$('#' + $input.data('target-input-id'));
                        if (hidden.length) hidden.val('').change();
                    }
                },
                focus: () => false,
                select: (event, item) => {
                    // Facture : le champ sert à alimenter le panier multi-BC, on le vide
                    // après chaque ajout pour permettre d'en enchaîner plusieurs.
                    if (!this.isCreditNote) {
                        this.addExpense(item.item);
                        this.autocompleteQuery = '';
                        window.$(event.target).val('').trigger('input');
                        setTimeout(() => { window.$(event.target).blur(); });
                        return false;
                    }

                    this.selectedMatchId = item.item.id;
                    this.matchMode = 'autocomplete';
                    this.autocompleteQuery = item.item.value;
                    const hidden = window.$('#' + $input.data('target-input-id'));
                    if (hidden.length) hidden.val(item.item.id).change();
                    window.$(event.target).val(item.item.value).trigger('input');
                    setTimeout(() => { window.$(event.target).blur(); });
                },
                create: function () {
                    window.$(this).data('ui-autocomplete')._renderItem = (ul, item) => {
                        const totalHt  = vm.fmtAmt(item.total_ht);
                        const totalTtc = vm.fmtAmt(item.total_ttc);
                        const designation = item.designation ? vm.truncate(item.designation, 60) : '';
                        const companyName = item.company && item.company.name ? item.company.name : '';

                        const html = `
                            <div class="d-flex align-items-center justify-content-between">
                                <div>
                                    <span class="ix-text-bold">${item.number || item.label || ''}</span>
                                    ${designation ? `<span class="ix-text-secondary ml-1" style="font-size: 0.9em;">${designation}</span>` : ''}
                                    ${companyName ? `<div class="ix-text-secondary" style="font-size: 0.85em;">${companyName}</div>` : ''}
                                </div>
                                <div class="text-right ml-3" style="min-width: 140px;">
                                    <div>${totalHt} HT</div>
                                    <div>${totalTtc} TTC</div>
                                    ${item.total_remaining !== undefined && item.total_remaining !== null
                                        ? `<div class="ix-text-secondary" style="font-size: 0.85em;">Restant ${vm.fmtAmt(item.total_remaining)}</div>`
                                        : ''}
                                </div>
                            </div>`;

                        return window.$('<li>').append(html).appendTo(ul);
                    };
                },
            }).focus(function () {
                $input.autocomplete('search', '');
            });
        },

        autocompleteHeaders() {
            const tokenMeta = document.head.querySelector('[name~=api-token][content]');
            const headers = {
                'Content-Type':     'application/json',
                'X-Requested-With': 'XMLHttpRequest',
                'Accept':           'application/json',
            };
            if (tokenMeta && tokenMeta.content) {
                headers.Authorization = 'Bearer ' + tokenMeta.content;
            }
            return headers;
        },

        renderCompanyAutocomplete(ul, item) {
            const labelSubcontractor = item.label_subcontractor ? '<br>' + item.label_subcontractor : '';
            const typeArea = item.type_area ? '<br>' + item.type_area : '';
            return window.$('<li>')
                .append('<div>' + item.label + `<span style='font-size: 10px'>${labelSubcontractor}${typeArea}</span></div>`)
                .appendTo(ul);
        },

        // ─── Validation et import ────────────────────────────────────────────

        confirmSync() {
            this.syncError = null;

            if (!this.form.date) {
                this.syncError = 'La date d\'émission est obligatoire.';
                return;
            }
            if (!this.computedTotalTtc) {
                this.syncError = 'Le total TTC est obligatoire.';
                return;
            }
            if (this.item.sync_status !== 'needs_update' && !this.selectedAddressId) {
                this.syncError = 'L\'adresse de facturation est obligatoire.';
                return;
            }

            const payload = {
                source:        this.item.source,
                external_id:   this.item.external_id,
                document_type: this.item.data.document_type,
                company_id:    this.selectedCompanyId || null,
                address_id:    this.selectedAddressId || null,
                override:      this.buildOverride(),
            };

            if (this.isCreditNote) {
                if (this.computedMatchMode !== 'none') {
                    payload.linked_bill_id = this.computedSelectedMatchId || null;
                }
            } else {
                if (this.computedMatchMode === 'expenses') {
                    payload.expenses = this.selectedExpenses.map(e => ({
                        id:     e.id,
                        amount: parseFloat(e.amount) || 0,
                    }));
                } else if (this.computedMatchMode === 'suggestion' || this.computedMatchMode === 'autocomplete') {
                    payload.expense_id = this.computedSelectedMatchId || null;
                } else if (this.computedMatchMode === 'generate') {
                    payload.generate_expense = true;
                }
            }

            this.syncing = true;

            fetch(this.syncUrl, {
                method:  'POST',
                headers: {
                    'Content-Type':     'application/json',
                    'X-CSRF-TOKEN':     this.csrfToken,
                    'X-Requested-With': 'XMLHttpRequest',
                },
                body: JSON.stringify(payload),
            })
                .then(r => r.json().then(data => ({ ok: r.ok, data })))
                .then(({ ok, data }) => {
                    if (!ok) {
                        this.syncError = data.message || 'Une erreur est survenue.';
                        return;
                    }
                    window.dispatchEvent(new CustomEvent('inbox-item-synced', { detail: data }));
                    this.close();
                    const label = this.isCreditNote ? 'Avoir importé' : (data.status === 'updated' ? 'Facture mise à jour' : 'Facture importée');
                    const suffix = data.number ? ' · ' + data.number : '';
                    if (window.showToast) window.showToast(label + suffix, 'success');
                })
                .catch(() => {
                    this.syncError = 'Erreur réseau, veuillez réessayer.';
                })
                .finally(() => { this.syncing = false; });
        },

        addVatRow() {
            if (!this.form.vats) this.form.vats = [];
            this.form.vats.push({ rate: 20, amount: 0 });
        },

        removeVatRow(index) {
            this.form.vats.splice(index, 1);
        },

        buildOverride() {
            const d  = this.item.data || {};
            const ov = {};

            if (this.form.reference   !== (d.invoice_number || '')) ov.reference   = this.form.reference;
            if (this.form.designation !== (d.label || ''))          ov.designation = this.form.designation;
            if (!this.isCreditNote && this.form.date !== this.normalizeDateForInput(d.date)) {
                ov.date = this.form.date;
            }
            if (this.form.deadline !== this.normalizeDateForInput(d.deadline)) {
                ov.deadline = this.form.deadline;
            }

            const htSrc  = parseFloat(d.currency_amount_before_tax) || 0;
            const ttcSrc = parseFloat(d.amount) || 0;

            if (parseFloat(this.form.amount_ht) !== htSrc)    ov.amount_ht  = parseFloat(this.form.amount_ht);
            if (this.computedTotalTtc            !== ttcSrc)   ov.amount_ttc = this.computedTotalTtc;

            // Toujours envoyer les lignes TVA pour que le backend structure correctement
            ov.vats = (this.form.vats || []).map(v => ({
                rate:   parseFloat(v.rate)   || 0,
                amount: parseFloat(v.amount) || 0,
            }));

            return ov;
        },

        confirmHold() {
            this.holding   = true;
            this.holdError = null;

            fetch(this.suggestMatchBaseUrl + '/' + this.item.id + '/hold', {
                method:  'POST',
                headers: { 'X-CSRF-TOKEN': this.csrfToken, 'X-Requested-With': 'XMLHttpRequest' },
            })
                .then(r => r.json().then(data => ({ ok: r.ok, data })))
                .then(({ ok, data }) => {
                    if (!ok) { this.holdError = data.message || 'Erreur lors de la mise en attente.'; return; }
                    this.item = Object.assign({}, this.item, { sync_status: 'on_hold' });
                    window.dispatchEvent(new CustomEvent('inbox-item-status-changed', { detail: { id: this.item.id, sync_status: 'on_hold' } }));
                })
                .catch(() => { this.holdError = 'Erreur réseau, veuillez réessayer.'; })
                .finally(() => { this.holding = false; });
        },

        confirmUnhold() {
            this.holding   = true;
            this.holdError = null;

            fetch(this.suggestMatchBaseUrl + '/' + this.item.id + '/unhold', {
                method:  'POST',
                headers: { 'X-CSRF-TOKEN': this.csrfToken, 'X-Requested-With': 'XMLHttpRequest' },
            })
                .then(r => r.json().then(data => ({ ok: r.ok, data })))
                .then(({ ok, data }) => {
                    if (!ok) { this.holdError = data.message || 'Erreur lors de la remise en traitement.'; return; }
                    this.item = Object.assign({}, this.item, { sync_status: data.status });
                    window.dispatchEvent(new CustomEvent('inbox-item-status-changed', { detail: { id: this.item.id, sync_status: data.status } }));
                })
                .catch(() => { this.holdError = 'Erreur réseau, veuillez réessayer.'; })
                .finally(() => { this.holding = false; });
        },
    },
};
</script>

<style scoped>
/* ── Overlay ───────────────────────────────────────────────────────────────── */
.bill-inbox-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.25);
    z-index: 1045;
}

/* ── Panel latéral ─────────────────────────────────────────────────────────── */
.bill-inbox-sidebar {
    position: fixed;
    top: 0;
    right: 0;
    height: 100vh;
    width: 96vw;
    background: #fff;
    box-shadow: -4px 0 24px rgba(0, 0, 0, 0.12);
    z-index: 1049;
    transform: translateX(100%);
    transition: transform 0.25s ease;
    display: flex;
    flex-direction: column;
}

.bill-inbox-sidebar.open {
    transform: translateX(0);
}

/* ── Layout interne split ──────────────────────────────────────────────────── */
.bill-inbox-sidebar-inner {
    display: flex;
    height: 100%;
    overflow: hidden;
}

/* Colonne gauche */
.bill-inbox-col-left {
    flex: 0 0 42%;
    width: 42%;
    display: flex;
    flex-direction: column;
    border-right: 1px solid #e8eaed;
    overflow: hidden;
}

/* Grands écrans */
@media (min-width: 1400px) {
    .bill-inbox-sidebar { width: 88vw; }
    .bill-inbox-col-left { flex: 0 0 34%; width: 34%; }
}

@media (min-width: 1800px) {
    .bill-inbox-sidebar { width: 88vw; }
    .bill-inbox-col-left { flex: 0 0 28%; width: 28%; }
}

/* Petits écrans */
@media (max-width: 768px) {
    .bill-inbox-sidebar { width: 100vw; }
    .bill-inbox-sidebar-inner { flex-direction: column; }
    .bill-inbox-col-left { flex: 0 0 auto; width: 100%; max-height: 60vh; }
}

/* ── Header ────────────────────────────────────────────────────────────────── */
.bill-inbox-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    border-bottom: 1px solid #e8eaed;
    flex-shrink: 0;
    gap: 8px;
}

/* ── Scroll gauche ─────────────────────────────────────────────────────────── */
.bill-inbox-scroll {
    flex: 1;
    overflow-y: auto;
    padding: 0;
}

/* ── Sections ──────────────────────────────────────────────────────────────── */
.bill-inbox-section {
    padding: 16px;
    border-bottom: 1px solid #f0f0f0;
}

.bill-inbox-section-title {
    font-size: 0.75em;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #6c757d;
    font-weight: 600;
    margin-bottom: 12px;
}

/* ── Footer bouton ─────────────────────────────────────────────────────────── */
.bill-inbox-footer {
    padding: 12px 16px;
    border-top: 1px solid #e8eaed;
    flex-shrink: 0;
    background: #fff;
}

/* ── Suggestions BC ────────────────────────────────────────────────────────── */
.bill-inbox-suggestion {
    border: 1px solid #e8eaed;
    border-radius: 6px;
    padding: 10px 12px;
    margin-bottom: 8px;
    background: #fff;
    transition: border-color 0.15s, background 0.15s;
}

.bill-inbox-suggestion.active {
    border-color: #3b82f6;
    background: #eff6ff;
}

.bill-inbox-suggestion-detail {
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid #e8eaed;
}

/* Aligne la case à cocher sur la 1re ligne de texte du BC, quelle que soit
   la hauteur du bloc (déplié ou non). */
.bill-inbox-suggestion-check {
    width: 14px;
    height: 14px;
    margin: 0;
    flex: 0 0 14px;
    align-self: flex-start;
    transform: translateY(3px);
    cursor: pointer;
}

/* Contenu rattaché au choix « commandes existantes » : le filet gauche montre
   que ce bloc dépend du radio au-dessus. */
.bill-inbox-match-panel {
    margin-left: 7px;
    padding-left: 14px;
    border-left: 2px solid #e8eaed;
}

/* ── Panier d'affectation multi-BC ─────────────────────────────────────────── */
.bill-inbox-affectation {
    border: 1px solid #bfdbfe;
    border-radius: 6px;
    background: #f8fbff;
    padding: 4px 10px;
}

.bill-inbox-affectation-title {
    padding: 8px 0 6px;
    font-size: 0.85em;
    font-weight: 600;
    color: #1e40af;
    border-bottom: 1px solid #bfdbfe;
}

.bill-inbox-affectation-row {
    padding: 8px 0;
    border-bottom: 1px solid #e8eaed;
}

.bill-inbox-affectation-row:last-of-type {
    border-bottom: none;
}

.bill-inbox-affectation-summary {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: 4px 10px;
    padding: 8px 0;
    border-top: 1px solid #bfdbfe;
    font-size: 0.85em;
}

.ix-text-orange {
    color: #c2410c;
}

/* ── Bandeau "ce qui a changé" en mode needs_update ────────────────────────── */
.bill-inbox-changes-divider {
    background: #fff7ed !important;
    border-top: 2px solid #fdba74 !important;
    border-bottom: 2px solid #fdba74 !important;
    color: #9a3412 !important;
}

.bill-inbox-changes {
    background: #fffbf5;
}

.bill-inbox-change-row {
    padding: 6px 0;
    border-bottom: 1px dashed #f0e0c5;
}

.bill-inbox-change-row:last-child {
    border-bottom: none;
}

.bill-inbox-change-old {
    color: #6c757d;
    text-decoration: line-through;
    font-size: 0.9em;
}

.bill-inbox-change-new {
    color: #9a3412;
    font-weight: 600;
}

/* ── Badge diff source ─────────────────────────────────────────────────────── */
.bill-inbox-diff-badge {
    display: inline-block;
    font-size: 0.72em;
    color: #d97706;
    background: #fffbeb;
    border: 1px solid #fcd34d;
    border-radius: 3px;
    padding: 1px 5px;
    margin-left: 4px;
    cursor: help;
    font-weight: 500;
}

/* ── Bandeau doublon potentiel ─────────────────────────────────────────────── */
.bill-inbox-duplicate-divider {
    background: #fff7ed !important;
    border-top: 2px solid #fb923c !important;
    border-bottom: 2px solid #fb923c !important;
    color: #9a3412 !important;
}

.bill-inbox-duplicates {
    background: #fff7ed;
}

.bill-inbox-duplicate-row {
    padding: 8px 10px;
    border: 1px solid #fed7aa;
    border-radius: 6px;
    margin-bottom: 6px;
    background: #fff;
}

.bill-inbox-duplicate-row:last-child {
    margin-bottom: 0;
}

/* ── Bandeau fournisseur non retrouvé ──────────────────────────────────────
   Bleu et non orange : c'est une action attendue, pas une alerte sur la donnée,
   contrairement aux bandeaux « a changé » et « doublon » ci-dessus. */
.bill-inbox-supplier-divider {
    background: #ebf6fb !important;
    border-top: 2px solid #7fbde8 !important;
    border-bottom: 2px solid #7fbde8 !important;
    color: #076bb2 !important;
}

.bill-inbox-supplier {
    background: #f7fbfe;
}

.bill-inbox-supplier-identity {
    padding: 8px 10px;
    border: 1px solid #cfe6f5;
    border-radius: 6px;
    background: #fff;
}

.bill-inbox-candidate {
    display: block;
    width: 100%;
    text-align: left;
    padding: 5px 8px;
    margin-bottom: 4px;
    border: 1px solid #dfe6ec;
    border-radius: 4px;
    background: #fff;
    font-size: 0.9em;
    line-height: 1.3;
    cursor: pointer;
}

.bill-inbox-candidate:hover,
.bill-inbox-candidate:focus {
    border-color: #077fd4;
    box-shadow: 0 0 0 2px rgba(7, 127, 212, 0.12);
}

.bill-inbox-candidate.is-strong {
    border-color: #9fd6bb;
}

/* Séparation nette du dernier recours : créer une fiche ne doit pas se confondre
   avec les pistes de rattachement au-dessus. */
.bill-inbox-supplier-create {
    margin-top: 14px;
    padding-top: 12px;
    border-top: 1px dashed #cfe6f5;
}

.bill-inbox-field-table {
    width: 100%;
    margin-top: 8px;
    font-size: 0.82em;
    background: #fff;
    border: 1px solid #dfe6ec;
    border-radius: 6px;
}

.bill-inbox-field-table th,
.bill-inbox-field-table td {
    padding: 4px 8px;
    border-bottom: 1px solid #eef2f5;
    vertical-align: top;
    text-align: left;
    font-weight: 400;
}

.bill-inbox-field-table thead th {
    font-size: 0.9em;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #758495;
}

.bill-inbox-field-table tr:last-child th,
.bill-inbox-field-table tr:last-child td {
    border-bottom: none;
}

.bill-inbox-field-table th {
    width: 38%;
    color: #758495;
}

.bill-inbox-link-done {
    padding: 8px 16px;
    background: #ebfbf2;
    border-top: 1px solid #a7dfc3;
    border-bottom: 1px solid #a7dfc3;
    color: #198855;
    font-size: 0.85em;
}

/* ── Réconciliation prod-up / source ───────────────────────────────────────
   Le bleu ne marque que la reprise depuis la source : conserver une valeur ne
   modifie rien et n'a pas à attirer l'œil. */
.bill-inbox-reconcile-summary {
    margin-top: 8px;
    font-size: 0.85em;
    color: #586069;
}

table.bill-inbox-reconcile {
    width: 100%;
    margin-top: 12px;
    font-size: 0.82em;
    border-collapse: collapse;
}

table.bill-inbox-reconcile thead th {
    padding: 0 6px 4px;
    font-size: 0.9em;
    font-weight: 500;
    text-align: left;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #9aa5b1;
}

table.bill-inbox-reconcile tbody tr,
table.bill-inbox-reconcile tr {
    border-top: 1px solid #e4ebf1;
}

table.bill-inbox-reconcile > tr > th {
    width: 24%;
    padding: 5px 6px;
    font-weight: 400;
    text-align: left;
    color: #586069;
    vertical-align: middle;
}

table.bill-inbox-reconcile td {
    width: 38%;
    padding: 3px 2px;
    vertical-align: middle;
}

.reconcile-cell {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    margin: 0;
    padding: 4px 6px;
    border-radius: 4px;
    color: #9aa5b1;
    cursor: pointer;
    word-break: break-word;
}

.reconcile-cell:hover {
    background: #f2f6f9;
}

.reconcile-cell input {
    margin-top: 2px;
    flex-shrink: 0;
}

.reconcile-cell.is-selected {
    color: #24292e;
    font-weight: 500;
}

.reconcile-cell .is-empty {
    font-style: italic;
}

/* La ligne entière se marque quand la source l'emporte : le tableau se lit en
   diagonale pour répondre à « qu'est-ce qui va changer ». */
table.bill-inbox-reconcile tr.is-updated td:last-child .reconcile-cell.is-selected {
    background: #ebf6fb;
    color: #076bb2;
}

table.bill-inbox-reconcile tr.is-updated > th {
    box-shadow: inset 2px 0 0 #077fd4;
    color: #076bb2;
}

/* ── Séparateur Import / Rapprochement ────────────────────────────────────── */
.bill-inbox-section-divider {
    padding: 8px 16px;
    background: #f0f4f8;
    border-top: 2px solid #d0dbe8;
    border-bottom: 2px solid #d0dbe8;
    font-size: 0.72em;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: #495a6e;
}

/* ── Autocomplete ──────────────────────────────────────────────────────────── */
.bill-inbox-autocomplete-item {
    padding: 6px 10px;
    border-radius: 4px;
    cursor: pointer;
    transition: background 0.1s;
}

.bill-inbox-autocomplete-item:hover,
.bill-inbox-autocomplete-item.active {
    background: #f0f4ff;
}
</style>
